import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDecodedParams } from "../misc/utils";
import { format } from "date-fns";
import { useAws } from "../aws";

const fmtDate = (ms) => {
  if (!ms) return "—";
  try {
    return format(new Date(Number(ms)), "yyyy-MM-dd");
  } catch (e) {
    return "—";
  }
};

const DonationResultPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { awsHttp } = useAws();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const { id } = useDecodedParams();

  // Values stashed before redirecting to Checkout (fallback display).
  const stash = (() => {
    try {
      return JSON.parse(window.sessionStorage.getItem("recurring")) || {};
    } catch (e) {
      return {};
    }
  })();
  const donation = (() => {
    try {
      return JSON.parse(window.sessionStorage.getItem("donation")) || {};
    } catch (e) {
      return {};
    }
  })();
  // The real subscription, resolved from the Checkout session id on the URL.
  // Only monthly (recurring) sessions resolve here; a one-off session returns 404.
  const [sub, setSub] = useState(null);
  // A one-off Checkout return carries a session_id but is NOT a subscription.
  const isMonthly = donation.frequency === "monthly" || !!(sub && sub.recurring_status);

  useEffect(() => {
    if (!sessionId || donation.frequency === "once") return;
    let alive = true;
    awsHttp({
      HTTPRequestMethod: "GET",
      APIFunction: "user/subscription",
      unauthorize: true,
      queryString: { session_id: sessionId },
      requestBody: null,
    })
      .then((res) => {
        if (!alive) return;
        const body = res?.data?.body;
        if (body && typeof body === "object") {
          setSub(body);
          if (body.manage_token) {
            window.sessionStorage.setItem(
              "recurring",
              JSON.stringify({ ...stash, manage_token: body.manage_token, d_id: body.d_id })
            );
          }
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const oneOff = (() => {
    try {
      return JSON.parse(window.sessionStorage.getItem("lastOneOff")) || {};
    } catch (e) {
      return {};
    }
  })();

  const isOneOff = donation.frequency === "once";
  // For a one-off return, use the one-off values ONLY. The "recurring" stash is
  // written on every monthly setup and never cleared, so mixing it into a one-off
  // receipt would show a stale monthly amount / the subscription's reference no.
  const amount = isOneOff
    ? (oneOff.amount ?? donation.donationAmount ?? 0)
    : (sub?.recurring_amount ?? sub?.amount ?? stash.amount ?? donation.donationAmount ?? 0);
  const cardText = sub?.card_brand ? `${sub.card_brand.toUpperCase()} •••• ${sub.card_last4}（每月自動扣款）` : "信用卡（每月自動扣款）";
  const nextChargeDate = sub?.next_charge_at ? fmtDate(sub.next_charge_at) : "處理中…";
  const manageToken = sub?.manage_token || stash.manage_token;
  const goManage = () => navigate(manageToken ? `/recurring?t=${manageToken}` : "/recurring");

  const RecurringRow = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #eee", fontSize: "14px" }}>
      <span style={{ color: "#666" }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div className="page-view event-detail">
      <img className="logo mt-2 mb-6" src="/logoPNB.png" alt="pause-and-breathe-logo" />
      <form
        key="userInfoForm"
        id="userInfoForm"
        onKeyPress={(e) => {
          e.key === "Enter" && e.preventDefault();
        }}
      >
        <div className={`card-view donation-card donation-result-card`} style={{ marginBottom: "40px" }}>
          {/* <h1 className="mb-1">{t("Donation")}</h1> */}
          {id === "2430e09" && isMonthly ? (
            <>
              {/* monthly recurring success view */}
              <h1 className="mb-1">每月捐款設立成功</h1>
              <h4>
                感謝您！您的每月定期捐款已成功設立，確認電郵及首次捐款收據已發送至您的郵箱。
              </h4>
              <div
                style={{
                  background: "#f7fbff",
                  border: "1px solid #bfddf1",
                  borderRadius: "8px",
                  padding: "8px 16px 4px",
                  margin: "16px 0",
                }}
              >
                <RecurringRow label="每月捐款金額" value={`港幣 $${Number(amount || 0).toLocaleString()}／月`} />
                <RecurringRow label="付款方式" value={cardText} />
                <RecurringRow label="下次扣款日期" value={nextChargeDate} />
              </div>
              <h4 style={{ fontSize: "14px", color: "#555" }}>
                此後每次成功扣款，捐款收據將自動發送至您的電郵。您可隨時取消每月捐款：按下方「管理我的每月捐款」，或電郵至
                <a href="mailto:hello@pausebreathe.org"> hello@pausebreathe.org</a>。
              </h4>
              <div style={{ marginTop: "20px", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button variant="outlined" onClick={goManage}>
                  管理我的每月捐款
                </Button>
              </div>
            </>
          ) : id === "2430e09" ? (
            <>
              <h1 className="mb-1">{t("Donation Success Result")}</h1>
              <h4>
                閣下的捐款已經完成，確認電郵已發送至您的郵箱。感謝您！ 如果對是項交易有任何問題，請與
                <a href="hello@pausebreathe.org">hello@pausebreathe.org</a> 聯絡。
              </h4>
            </>
          ) : id === "failed" ? (
            <>
              <h1 className="mb-1">{t("Donation Fail Result")}</h1>
              <h4>
                抱歉，此操作已過時並關閉，請重新設定。如果對是項操作有任何問題，請與
                <a href="hello@pausebreathe.org">hello@pausebreathe.org</a> 聯絡。
              </h4>
            </>
          ) : id === "fail" ? (
            <>
              <h1 className="mb-1">{t("Donation Transaction Fail Result")}</h1>
              <h4>
                抱歉，系統或輸入資料出錯，請重新輸入。 如果對事項操作有問任何問題，請與
                <a href="hello@pausebreathe.org">hello@pausebreathe.org</a> 聯絡。
              </h4>
            </>
          ) : null}
        </div>
        <div className={`my-5 btns-gp-div`}>
          <Button
            sx={{ marginRight: 2 }}
            onClick={() => {
              navigate("/donation");
            }}
          >
            {t("Back")}
          </Button>
        </div>
      </form>
      {/* <div>{`event ${id || "none"}`}</div> */}
    </div>
  );
};

export default DonationResultPage;
